import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'CustomCmsIntegration',
  data () {
    return {
      isLoading: false,
      isDeleting: false,
      apiBaseUrl: '',
      apiKey: '',
      postEndpoint: '',
      getEndpoint: '',
      putEndpoint: '',
      fieldMapping: {
        title: '',
        content: '',
        slug: '',
        featured_image: '',
        published_at: '',
        author: ''
      },
      availableFields: [
        { key: 'title', label: 'Title', description: 'Article title (string)' },
        { key: 'content', label: 'Content', description: 'Main article content (HTML string)' },
        { key: 'slug', label: 'Slug', description: 'URL slug for the article (string)' },
        { key: 'featured_image', label: 'Featured Image', description: 'Featured image URL (string)' },
        { key: 'published_at', label: 'Published Date', description: 'Publication date (YYYY-MM-DD)' },
        { key: 'author', label: 'Author', description: 'Article author (string)' }
      ],
      rules: {
        required: value => !!value || 'Required.',
        validUrl: value => {
          const pattern = /^https?:\/\/.+/
          return pattern.test(value) || 'Please enter a valid URL starting with http:// or https://'
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      'customCmsIntegration'
    ]),
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    activeIntegrationUrl () {
      return this.customCmsIntegration?.custom_cms_api_base_url
    },
    isValidToSave () {
      return !!this.apiBaseUrl && !!this.apiKey && this.isValidUrl(this.apiBaseUrl)
    },
    hasFieldMapping () {
      return Object.values(this.fieldMapping).some(value => value)
    }
  },
  methods: {
    ...mapActions([
      'getIntegrations',
      'postCustomCmsIntegration',
      'deleteIntegration'
    ]),
    ...mapMutations([
      'setSnackbar'
    ]),
    isValidUrl (url) {
      const pattern = /^https?:\/\/.+/
      return pattern.test(url)
    },
    async saveIntegration () {
      if (!this.isValidToSave) return

      this.isLoading = true
      try {
        await this.postCustomCmsIntegration({
          contentplanId: this.contentplanId,
          apiBaseUrl: this.apiBaseUrl,
          apiKey: this.apiKey,
          postEndpoint: this.postEndpoint,
          putEndpoint: this.putEndpoint,
          getEndpoint: this.getEndpoint,
          fieldMapping: this.hasFieldMapping ? this.fieldMapping : null,
          type: 'custom-cms'
        })

        // Refresh integrations state
        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Custom CMS integration saved!', type: 'success' })
      } catch (error) {
        console.error('Error saving Custom CMS integration:', error)
        this.setSnackbar({
          open: true,
          text: `Error: ${error?.response?.data?.message || error.message || 'Could not save integration'}`,
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    },
    initializeFromIntegration (integration) {
      if (!integration || integration.type !== 'custom-cms') return
      this.apiBaseUrl = integration.custom_cms_api_base_url || ''
      this.apiKey = '' // Keep API Key blank for security
      this.postEndpoint = integration.custom_cms_post_endpoint || ''
      this.getEndpoint = integration.custom_cms_get_endpoint || ''
      this.putEndpoint = integration.custom_cms_put_endpoint || ''

      // Parse field mapping if it exists
      if (integration.custom_cms_field_mapping) {
        try {
          const mapping = typeof integration.custom_cms_field_mapping === 'string'
            ? JSON.parse(integration.custom_cms_field_mapping)
            : integration.custom_cms_field_mapping
          this.fieldMapping = { ...this.fieldMapping, ...mapping }
        } catch (error) {
          console.error('Error parsing field mapping:', error)
        }
      }
    },
    clearFieldMapping () {
      this.fieldMapping = {
        title: '',
        content: '',
        slug: '',
        featured_image: '',
        published_at: '',
        author: ''
      }
    },
    async handleDelete () {
      if (!this.activeIntegrationUrl || this.isDeleting) return
      if (!window.confirm('are you sure you want to remove integration')) return

      this.isDeleting = true
      try {
        await this.deleteIntegration({
          contentplanId: this.contentplanId,
          integrationType: 'custom-cms'
        })
        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Integration removed.', type: 'success' })
        this.$router.push({
          name: 'ContentplanSettings',
          params: { contentplanId: this.contentplanId, workspaceId: this.$route.params.workspaceId }
        })
      } catch (error) {
        this.setSnackbar({ open: true, text: `Error: ${error?.response?.data?.message || error.message || 'Could not remove integration'}`, type: 'error' })
      } finally {
        this.isDeleting = false
      }
    }
  },
  async mounted () {
    await this.getIntegrations({ contentplanId: this.contentplanId })
    this.initializeFromIntegration(this.customCmsIntegration)
  }
}
